import {
  Button,
  Flex,
  Heading,
  Modal,
  Paragraph,
  Select,
  Span,
  type SelectOption,
} from "@ir/ui";

export interface BrandDetailsAccountingIntegrationContentProps {
  loading: boolean;
  error?: string | null;
  connecting: boolean;
  disconnecting: boolean;
  checkingStatus: boolean;
  selectedKey: string;
  options: SelectOption[];
  selectedOption: SelectOption | undefined;
  onSelectedKeyChange: (key: string) => void;
  pendingKey: string | null;
  pendingOption: SelectOption | undefined;
  switching: boolean;
  onConfirmSwitch: () => void;
  onCancelSwitch: () => void;
}

const NO_SELECTION_OPTION: SelectOption = {
  value: "",
  label: "Select accounting",
};

function parseSelectValue(value: unknown): string {
  if (value == null) return "";

  const raw =
    typeof value === "object" && "value" in (value as object)
      ? (value as SelectOption).value
      : value;

  if (typeof raw === "string") return raw;
  if (
    typeof raw === "number" ||
    typeof raw === "boolean" ||
    typeof raw === "bigint"
  ) {
    return String(raw);
  }
  return "";
}

export function BrandDetailsAccountingIntegrationContent({
  loading,
  error,
  connecting,
  disconnecting,
  checkingStatus,
  selectedKey,
  options,
  selectedOption,
  onSelectedKeyChange,
  pendingKey,
  pendingOption,
  switching,
  onConfirmSwitch,
  onCancelSwitch,
}: Readonly<BrandDetailsAccountingIntegrationContentProps>) {
  const currentLabel =
    typeof selectedOption?.label === "string"
      ? selectedOption.label
      : "the current accounting system";
  const pendingLabel =
    typeof pendingOption?.label === "string" ? pendingOption.label : null;

  return (
    <Flex align="center" gap={0.5} className="py-3">
      <Paragraph className="text-grey-500 font-bold m-0 mb-2">
        Accounting System
      </Paragraph>
      {error ? (
        <Paragraph className="text-danger m-0 mb-2">{error}</Paragraph>
      ) : null}
      <Select
        key={`accounting-system-${selectedKey || "none"}-${options.length}-${loading}-${pendingKey ?? ""}`}
        size="medium"
        isSearchable={false}
        isClearable={false}
        isDisabled={
          loading ||
          options.length === 0 ||
          connecting ||
          disconnecting ||
          checkingStatus ||
          switching
        }
        placeholder={loading ? "Loading..." : "Select accounting"}
        options={[NO_SELECTION_OPTION, ...options]}
        defaultValue={
          loading ? undefined : (selectedOption ?? NO_SELECTION_OPTION)
        }
        onChange={(next) => onSelectedKeyChange(parseSelectValue(next))}
        aria-label="Accounting system"
      />

      <Modal
        isOpen={pendingKey != null}
        onClose={() => {
          if (!switching) onCancelSwitch();
        }}
        size="small"
        closable={!switching}
        headerContent={
          <Heading level={6} className="text-grey-700 font-bold m-0">
            Switch accounting system
          </Heading>
        }
        footerContent={
          <Flex gap={0.5} className="flex-1" justifyContent="space-between">
            <Button
              size="small"
              color="grey"
              isDisabled={switching}
              onPress={onCancelSwitch}
            >
              Cancel
            </Button>
            <Button
              size="small"
              color="error"
              isLoading={switching}
              isDisabled={switching}
              onPress={onConfirmSwitch}
            >
              Confirm
            </Button>
          </Flex>
        }
        classNames={{ body: "p-0" }}
      >
        <Paragraph className="m-0 py-4 px-3">
          Switching the accounting system will disconnect the existing
          connection with{" "}
          <Span className="font-bold text-primary-500">{currentLabel}</Span>
          {pendingLabel ? (
            <>
              {" "}
              and select{" "}
              <Span className="font-bold text-primary-500">
                {pendingLabel}
              </Span>
            </>
          ) : null}
          . Do you want to continue?
        </Paragraph>
      </Modal>
    </Flex>
  );
}
